// The inputs a format's brief asks for, rendered from its `briefFields` by `input` (spec-v3-formats.md, Web E1):
// the idea as a long text, looks as visual swatches, lengths as chips, aspects with their frame shapes, a characters
// editor, photo and song pickers from the library, and choice chips. Used by the new-video sheet and the project's
// Brief panel, so both ask the same way.
import { api } from "../api";
import { toggleAudio } from "../audio";
import { ASPECT_FOR, ASPECT_WORD, fmtLen, type BriefField, type Character, type FormatInfo, type VideoBriefDraft } from "../formats";
import { Link } from "../router";
import { useStore } from "../store";
import type { Aspect, Asset } from "../types";
import { Icon, Spinner, useLoad } from "./bits";

type Props = {
  field: BriefField;
  format: FormatInfo;
  brief: VideoBriefDraft;
  onChange: (patch: VideoBriefDraft) => void;
  idPrefix?: string;
  /** v4 (V4-M1): the song field's "I'll make it in Suno" choice (the new-video sheet) */
  suno?: { on: boolean; set: (on: boolean) => void };
};

/** A short label for a field (the question is the long form). */
export const FIELD_LABEL: Record<string, string> = {
  idea: "The idea",
  sources: "Facts and sources",
  tone: "Feel",
  audience: "Audience",
  durationS: "Length",
  aspect: "Shape",
  look: "Look",
  title: "Title",
  variant: "Kind",
  characters: "Characters",
  photoAssetIds: "Photos",
  songAssetId: "Song",
  lyrics: "Lyrics",
  cta: "Call to action",
};
export const fieldLabel = (f: BriefField) => FIELD_LABEL[f.key] ?? f.key;

export function BriefInput({ field, format, brief, onChange, idPrefix = "bf", suno }: Props) {
  const id = `${idPrefix}-${field.key}`;
  const head = (
    <span className="bf-head">
      <span className="field-label" id={`${id}-l`}>
        {field.question}
      </span>
      {!field.required && <span className="bf-optional">Optional</span>}
    </span>
  );
  const val = (brief as Record<string, unknown>)[field.key];
  switch (field.input) {
    case "text":
    case "longtext": {
      const long = field.input === "longtext";
      const v = typeof val === "string" ? val : "";
      return (
        <label className="field bf" htmlFor={id}>
          {head}
          {long ? (
            <textarea id={id} value={v} rows={field.key === "idea" ? 4 : 3} placeholder={field.placeholder ?? ""} onChange={(e) => onChange({ [field.key]: e.target.value })} data-autofocus={field.key === "idea" ? true : undefined} />
          ) : (
            <input id={id} value={v} placeholder={field.placeholder ?? ""} onChange={(e) => onChange({ [field.key]: e.target.value })} />
          )}
          {field.key === "sources" && <span className="field-hint">Paste notes, quotes or links. Only facts from here are stated as facts.</span>}
        </label>
      );
    }
    case "choice":
      return (
        <div className="field bf" role="group" aria-labelledby={`${id}-l`}>
          {head}
          <ChoiceChips choices={field.choices ?? []} value={typeof val === "string" ? val : undefined} onChange={(v) => onChange({ [field.key]: v })} />
        </div>
      );
    case "duration":
      return (
        <div className="field bf" role="group" aria-labelledby={`${id}-l`}>
          {head}
          <LengthChips durations={format.durations} value={brief.durationS} onChange={(d) => onChange({ durationS: d })} />
        </div>
      );
    case "aspect":
      return (
        <div className="field bf" role="group" aria-labelledby={`${id}-l`}>
          {head}
          <AspectPicker aspects={format.aspects} value={brief.aspect} onChange={(a) => onChange({ aspect: a })} />
        </div>
      );
    case "look":
      return (
        <div className="field bf" role="group" aria-labelledby={`${id}-l`}>
          {head}
          <LookSwatches format={format} value={brief.look} onChange={(l) => onChange({ look: l })} />
        </div>
      );
    case "characters":
      return (
        <div className="field bf" role="group" aria-labelledby={`${id}-l`}>
          {head}
          <CharactersEditor value={brief.characters ?? []} onChange={(c) => onChange({ characters: c })} hosts={format.voices === "hosts"} />
        </div>
      );
    case "photos":
      return (
        <div className="field bf" role="group" aria-labelledby={`${id}-l`}>
          {head}
          <PhotoPicker value={brief.photoAssetIds ?? []} onChange={(ids) => onChange({ photoAssetIds: ids })} />
        </div>
      );
    case "song":
      return (
        <div className="field bf" role="group" aria-labelledby={`${id}-l`}>
          {head}
          <SongPicker
            value={brief.songAssetId}
            onChange={(sid) => {
              suno?.set(false);
              onChange({ songAssetId: sid });
            }}
            {...(suno ? { suno: suno.on, onSuno: () => (suno.set(true), onChange({ songAssetId: undefined })) } : {})}
          />
        </div>
      );
  }
}

export function ChoiceChips({ choices, value, onChange }: { choices: string[]; value?: string; onChange: (v: string) => void }) {
  return (
    <span className="bf-chips" role="radiogroup">
      {choices.map((c) => (
        <button data-action="brief.choose" key={c} type="button" role="radio" aria-checked={c === value} className={`chip${c === value ? " is-on" : ""}`} onClick={() => onChange(c)}>
          {c.replace(/^./, (x) => x.toUpperCase())}
        </button>
      ))}
    </span>
  );
}

export function LengthChips({ durations, value, onChange }: { durations: number[]; value?: number; onChange: (d: number) => void }) {
  const sorted = [...durations].sort((a, b) => a - b);
  return (
    <span className="bf-chips" role="radiogroup">
      {sorted.map((d) => (
        <button data-action="brief.choose" key={d} type="button" role="radio" aria-checked={d === value} className={`chip${d === value ? " is-on" : ""}`} onClick={() => onChange(d)}>
          {fmtLen(d)}
        </button>
      ))}
    </span>
  );
}

export function AspectPicker({ aspects, value, onChange }: { aspects: Aspect[]; value?: Aspect; onChange: (a: Aspect) => void }) {
  return (
    <span className="bf-aspects" role="radiogroup">
      {aspects.map((a) => (
        <button data-action="brief.choose" key={a} type="button" role="radio" aria-checked={a === value} className={`bf-aspect${a === value ? " is-on" : ""}`} onClick={() => onChange(a)}>
          <span className={`bf-frame bf-frame-${a.replace(":", "x")}`} aria-hidden />
          <span className="bf-aspect-text">
            <strong>{ASPECT_WORD[a]}</strong>
            <span>{ASPECT_FOR[a]}</span>
          </span>
        </button>
      ))}
    </span>
  );
}

export function LookSwatches({ format, value, onChange }: { format: FormatInfo; value?: string; onChange: (id: string) => void }) {
  return (
    <ul className="looks" role="radiogroup">
      {format.looks.map((l) => {
        const on = l.id === (value ?? format.looks[0]?.id);
        return (
          <li key={l.id}>
            <button data-action="brief.look" type="button" role="radio" aria-checked={on} className={`look${on ? " is-on" : ""}`} onClick={() => onChange(l.id)} title={l.description}>
              <span className={`swatch swatch-${l.id} swatch-in-${format.id}`} aria-hidden>
                {on && (
                  <span className="look-check">
                    <Icon name="check" size={14} />
                  </span>
                )}
              </span>
              <span className="look-name">{l.name}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

let nextChar = 0;
export const newCharacter = (taken: Character[]): Character => {
  let id = "";
  do id = `c${taken.length + 1 + nextChar++}`;
  while (taken.some((c) => c.id === id));
  return { id, name: "", look: "", voice: "" };
};

/** The cast before the project exists: a name, what they look like, how they sound. */
export function CharactersEditor({ value, onChange, hosts = false, max = 6 }: { value: Character[]; onChange: (c: Character[]) => void; hosts?: boolean; max?: number }) {
  const set = (i: number, patch: Partial<Character>) => onChange(value.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  const limit = hosts ? 2 : max;
  return (
    <div className="chars">
      {value.map((c, i) => (
        <fieldset key={c.id} className="char-row">
          <legend className="sr-only">{c.name || `Character ${i + 1}`}</legend>
          <span className="char-avatar" aria-hidden>
            {(c.name || "?").slice(0, 1).toUpperCase()}
          </span>
          <div className="char-fields">
            <input value={c.name} onChange={(e) => set(i, { name: e.target.value })} placeholder={hosts ? `Host ${i + 1}'s name` : "Name"} aria-label="Name" maxLength={60} />
            <input value={c.look} onChange={(e) => set(i, { look: e.target.value })} placeholder="Looks like: a small red fox with a striped scarf" aria-label="What they look like" maxLength={600} />
            <input value={c.voice} onChange={(e) => set(i, { voice: e.target.value })} placeholder="Sounds like: a bright, nervous kid" aria-label="How they sound" maxLength={200} />
          </div>
          <button type="button" className="icon-btn" aria-label={`Remove ${c.name || "this character"}`} onClick={() => onChange(value.filter((_, k) => k !== i))}>
            <Icon name="close" size={18} />
          </button>
        </fieldset>
      ))}
      {value.length < limit && (
        <button type="button" className="btn btn-quiet btn-small chars-add" onClick={() => onChange([...value, newCharacter(value)])}>
          <Icon name="plus" size={16} /> {value.length ? (hosts ? "Add the second host" : "Add another character") : hosts ? "Add a host" : "Add a character"}
        </button>
      )}
      {!value.length && <span className="field-hint">Or leave it empty and the script invents them. You can change anyone later.</span>}
    </div>
  );
}

/** The user's pictures from the library, picked in order (the number is where each one appears). */
export function PhotoPicker({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const { data, loading } = useLoad<Asset[]>(() => api.library({ kind: "image" }).catch(() => []), []);
  if (loading && !data) return <Spinner label="Loading your pictures" />;
  if (!data?.length)
    return (
      <p className="bf-empty">
        No pictures in your <Link to="/library">Library</Link> yet. Add some there, or skip this and the Director paints them.
      </p>
    );
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <>
      <ul className="photos" aria-label="Your pictures">
        {data.map((a) => {
          const n = value.indexOf(a.id);
          return (
            <li key={a.id}>
              <button data-action="brief.pick" type="button" className={`photo${n >= 0 ? " is-on" : ""}`} aria-pressed={n >= 0} onClick={() => toggle(a.id)} aria-label={`${a.name}${n >= 0 ? `, picture ${n + 1}` : ""}`}>
                {a.thumbUrl || a.url ? <img src={a.thumbUrl || a.url || ""} alt="" loading="lazy" /> : <span className="photo-blank" />}
                {n >= 0 && <span className="photo-n">{n + 1}</span>}
              </button>
            </li>
          );
        })}
      </ul>
      <span className="field-hint">{value.length ? `${value.length} picked. They appear in the order you tap them.` : "Tap pictures in the order they should appear."}</span>
    </>
  );
}

/** A song from the library, "make one for me", or (v4, V4-M1, with `onSuno`) "I'll make it in Suno". */
export function SongPicker({ value, onChange, suno = false, onSuno }: { value?: string; onChange: (id: string | undefined) => void; suno?: boolean; onSuno?: () => void }) {
  const { data, loading } = useLoad<Asset[]>(() => api.library({ kind: "music" }).catch(() => []), []);
  const playing = useStore((s) => s.playing);
  const hint = "The video cuts to its beat.";
  if (loading && !data) return <Spinner label="Loading your music" />;
  return (
    <ul className="songs" role="radiogroup">
      <li>
        <button type="button" role="radio" aria-checked={!value && !suno} className={`song${!value && !suno ? " is-on" : ""}`} onClick={() => onChange(undefined)}>
          <span className="song-icon" aria-hidden>
            <Icon name="bolt" size={16} />
          </span>
          <span className="song-text">
            <strong>Make one for me</strong>
            <span>From the mood you describe</span>
          </span>
        </button>
      </li>
      {onSuno && (
        <li>
          <button type="button" role="radio" aria-checked={suno} className={`song${suno ? " is-on" : ""}`} onClick={onSuno}>
            <span className="song-icon" aria-hidden>
              <Icon name="music" size={16} />
            </span>
            <span className="song-text">
              <strong>I'll make it in Suno</strong>
              <span>We write the prompts, you make the song and upload it</span>
            </span>
          </button>
        </li>
      )}
      {(data ?? []).map((a) => {
        const on = a.id === value && !suno;
        const key = `song-${a.id}`;
        return (
          <li key={a.id} className="song-li">
            <button data-action="brief.pick" type="button" role="radio" aria-checked={on} className={`song${on ? " is-on" : ""}`} onClick={() => onChange(a.id)}>
              <span className="song-icon" aria-hidden>
                <Icon name="music" size={16} />
              </span>
              <span className="song-text">
                <strong>{a.name}</strong>
                <span>{on ? hint : a.tags.join(", ") || "From your library"}</span>
              </span>
            </button>
            <button type="button" className={`icon-btn song-play${playing === key ? " is-on" : ""}`} aria-label={playing === key ? `Stop ${a.name}` : `Play ${a.name}`} onClick={() => toggleAudio(key, a.url)}>
              <Icon name={playing === key ? "pause" : "play"} size={16} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
